import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { VmoV2State } from './VmoV2.types';

const initialState: VmoV2State = {
  // Credit Union flags
  openCreditUnionModal: false,
  isCreditUnion: false,
  acceptedCreditUnion: false,
  hasSeenCUModal: false,

  // Debt Consolidation flags
  openDebtConsolidationModal: false,
  acceptedDebtConsolidation: false,
  hasSeenDebtConsolidationModal: false,

  // Existing state
  selectedProgram: [],
  currentIndex: 0,
  typeOfProgram: '',
  programs: [],
  isReprice: false,
  disableRepriceButton: true,
  minDrawAmount: 0,
  maxDrawAmount: 0,
  initialLineAmount: 0,
  auxLineAmount: 0,
  drawAmount: 0,
  selectedLineAmount: 0,
  selectedDrawAmount: 0,
  minLineAmount: 0,
  maxLineAmount: 0,
  minCalculatedDrawAmount: 0,
  includeCheckboxes: {},
  removeCheckboxes: {},
  vmoInputValue: '$0',
  isAtLeastOneRepriceDone: false,
  isChange: true,
};

export const VmoV2Slice = createSlice({
  name: 'VmoV2',
  initialState,
  reducers: {
    setOpenCreditUnionModal: (state, action: PayloadAction<boolean>) => {
      state.openCreditUnionModal = action.payload;
    },
    setIsCreditUnion: (state, action: PayloadAction<boolean>) => {
      state.isCreditUnion = action.payload;
    },
    setAcceptedCreditUnion: (state, action: PayloadAction<boolean>) => {
      state.acceptedCreditUnion = action.payload;
    },
    setHasSeenCUModal: (state, action: PayloadAction<boolean>) => {
      state.hasSeenCUModal = action.payload;
    },

    setOpenDebtConsolidationModal: (state, action: PayloadAction<boolean>) => {
      state.openDebtConsolidationModal = action.payload;
    },
    setAcceptedDebtConsolidation: (state, action: PayloadAction<boolean>) => {
      state.acceptedDebtConsolidation = action.payload;
    },
    setHasSeenDebtConsolidationModal: (state, action: PayloadAction<boolean>) => {
      state.hasSeenDebtConsolidationModal = action.payload;
    },

    setSelectedProgram: (state, action: PayloadAction<number[]>) => {
      state.selectedProgram = action.payload;
      if (action.payload.length === 0) {
        // reset when program is deselected
        state.openDebtConsolidationModal = false;
        state.acceptedDebtConsolidation = false;
        state.hasSeenDebtConsolidationModal = false;
        state.openCreditUnionModal = false;
        state.isCreditUnion = false;
        state.acceptedCreditUnion = false;
        state.hasSeenCUModal = false;
        state.disableRepriceButton = true;
      }
    },
    setCurrentIndex: (state, action: PayloadAction<number>) => {
      state.currentIndex = action.payload;
    },
    setTypeOfProgram: (state, action: PayloadAction<string>) => {
      state.typeOfProgram = action.payload;
    },
    setPrograms: (state, action: PayloadAction<any[]>) => {
      state.programs = action.payload;
    },
    setIsReprice: (state, action: PayloadAction<boolean>) => {
      state.isReprice = action.payload;
    },
    setDisableRepriceButton: (state, action: PayloadAction<boolean>) => {
      state.disableRepriceButton = action.payload;
    },
    setMinDrawAmount: (state, action: PayloadAction<number>) => {
      state.minDrawAmount = action.payload;
    },
    setMaxDrawAmount: (state, action: PayloadAction<number>) => {
      state.maxDrawAmount = action.payload;
    },
    setInitialLineAmount: (state, action: PayloadAction<number>) => {
      state.initialLineAmount = action.payload;
    },
    setAuxLineAmount: (state, action: PayloadAction<number>) => {
      state.auxLineAmount = action.payload;
    },
    setDrawAmount: (state, action: PayloadAction<number>) => {
      state.drawAmount = action.payload;
    },
    setSelectedLineAmount: (state, action: PayloadAction<number>) => {
      state.selectedLineAmount = action.payload;
    },
    setSelectedDrawAmount: (state, action: PayloadAction<number>) => {
      state.selectedDrawAmount = action.payload;
    },
    setMinLineAmount: (state, action: PayloadAction<number>) => {
      state.minLineAmount = action.payload;
    },
    setMaxLineAmount: (state, action: PayloadAction<number>) => {
      state.maxLineAmount = action.payload;
    },
    setMinCalculatedDrawAmount: (state, action: PayloadAction<number>) => {
      state.minCalculatedDrawAmount = action.payload;
    },
    setIncludeCheckboxes: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.includeCheckboxes = action.payload;
    },
    setRemoveCheckboxes: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.removeCheckboxes = action.payload;
    },
    updateIncludeCheckbox: (state, action: PayloadAction<{ id: string; value: boolean }>) => {
      state.includeCheckboxes[action.payload.id] = action.payload.value;
    },
    updateRemoveCheckbox: (state, action: PayloadAction<{ id: string; value: boolean }>) => {
      state.removeCheckboxes[action.payload.id] = action.payload.value;
    },
    clearCheckboxes: (state) => {
      state.includeCheckboxes = {};
      state.removeCheckboxes = {};
    },
    clearSelectedProgramStatus: (state) => {
      state.selectedProgram = [];
      // reset when deselected
      state.openDebtConsolidationModal = false;
      state.acceptedDebtConsolidation = false;
      state.hasSeenDebtConsolidationModal = false;
      state.openCreditUnionModal = false;
      state.isCreditUnion = false;
      state.acceptedCreditUnion = false;
      state.hasSeenCUModal = false;
      state.isAtLeastOneRepriceDone = false;
      // state.disableRepriceButton = true;
    },
    setVmoInputValue: (state, action: PayloadAction<string>) => {
      state.vmoInputValue = action.payload;
    },
    setIsAtLeastOneRepriceDone: (state, action: PayloadAction<boolean>) => {
      state.isAtLeastOneRepriceDone = action.payload;
    },
    setIsChange: (state, action: PayloadAction<boolean>) => {
      state.isChange = action.payload;
    },
    resetState: (_state) => {
      return initialState;
    },
  },
});

export const {
  setOpenCreditUnionModal,
  setIsCreditUnion,
  setAcceptedCreditUnion,
  setHasSeenCUModal,
  setOpenDebtConsolidationModal,
  setAcceptedDebtConsolidation,
  setHasSeenDebtConsolidationModal,
  setSelectedProgram,
  setCurrentIndex,
  setTypeOfProgram,
  setPrograms,
  setIsReprice,
  setDisableRepriceButton,
  setMinDrawAmount,
  setMaxDrawAmount,
  setInitialLineAmount,
  setAuxLineAmount,
  setDrawAmount,
  setSelectedLineAmount,
  setSelectedDrawAmount,
  setMinLineAmount,
  setMaxLineAmount,
  setMinCalculatedDrawAmount,
  setIncludeCheckboxes,
  setRemoveCheckboxes,
  updateIncludeCheckbox,
  updateRemoveCheckbox,
  clearCheckboxes,
  clearSelectedProgramStatus,
  resetState,
  setVmoInputValue,
  setIsAtLeastOneRepriceDone,
  setIsChange,
} = VmoV2Slice.actions;

export default VmoV2Slice.reducer;
